import { useRef } from 'react'
import { motion } from 'framer-motion'
import '@fontsource-variable/playfair-display'
import { useI18n } from '@/lib/i18n'
import { BgVideo, PhraseSwap, clamp01, easeOutCubic, rand, useCanvasLoop, useCtaText, useFrame, usePhraseCycle, type SaverProps } from './shared'

const GOLD = '#d4b483'
const CYCLE = 10
const FOCUS_PULL = 2.4
const AF_POINTS = Array.from({ length: 15 }, (_, i) => i)
const AF_LOCK = new Set([6, 7, 8]) // middle row centre: where a face would be

interface Speck { x: number; y: number; r: number; vx: number; vy: number; phase: number }

export default function BusinessSaver({ videoSrc, phrases, intervalSeconds, wakeMethod }: SaverProps) {
  const { t } = useI18n()
  const cta = useCtaText(wakeMethod)
  const phrase = usePhraseCycle(phrases, Math.max(4, intervalSeconds * 2))
  const videoRef = useRef<HTMLVideoElement>(null)
  const afRefs = useRef<(HTMLDivElement | null)[]>([])
  const lockRef = useRef<HTMLDivElement>(null)
  const focusTextRef = useRef<HTMLSpanElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const specks = useRef<Speck[]>([])

  useFrame((time) => {
    const c = time % CYCLE
    // Focus pull: soft, then sharp; drifts soft again just before the cycle ends.
    const sharp = c < FOCUS_PULL ? easeOutCubic(c / FOCUS_PULL) : c > CYCLE - 1.2 ? 1 - (c - (CYCLE - 1.2)) / 1.2 : 1
    const blur = (1 - sharp) * 12
    if (videoRef.current) videoRef.current.style.filter = `grayscale(0.8) brightness(0.55) contrast(1.05) blur(${blur.toFixed(2)}px)`
    const locked = c >= FOCUS_PULL && c < CYCLE - 1.2
    for (let i = 0; i < AF_POINTS.length; i++) {
      const el = afRefs.current[i]
      if (!el) continue
      const hunting = !locked && Math.sin(time * 14 + i * 2.3) > 0.7
      const on = locked ? AF_LOCK.has(i) : hunting
      el.style.borderColor = on ? GOLD : 'rgba(255,255,255,0.22)'
      el.style.background = on && locked ? 'rgba(212,180,131,0.18)' : 'transparent'
    }
    if (lockRef.current) {
      const k = locked ? clamp01((c - FOCUS_PULL) / 0.25) : 0
      lockRef.current.style.opacity = String(k)
      lockRef.current.style.transform = `translate(-50%, -50%) scale(${(1.15 - 0.15 * easeOutCubic(k)).toFixed(3)})`
    }
    if (focusTextRef.current) focusTextRef.current.textContent = locked ? 'AF ● LOCK' : 'AF ○'
  })

  useCanvasLoop(canvasRef, (ctx, w, h, time, dt) => {
    if (specks.current.length === 0) {
      for (let i = 0; i < 46; i++) specks.current.push({ x: rand(0, w), y: rand(0, h), r: rand(0.6, 1.8), vx: rand(-4, 4), vy: rand(-7, -2), phase: rand(0, 6.28) })
    }
    for (const s of specks.current) {
      s.x += s.vx * dt
      s.y += s.vy * dt
      if (s.y < -5) { s.y = h + 5; s.x = rand(0, w) }
      if (s.x < -5) s.x = w + 5
      if (s.x > w + 5) s.x = -5
      const a = 0.15 + 0.45 * (0.5 + 0.5 * Math.sin(time * 1.4 + s.phase))
      ctx.fillStyle = `rgba(226, 196, 146, ${a.toFixed(3)})`
      ctx.beginPath()
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
      ctx.fill()
    }
  })

  const words = t('screensaver.hero.business').split(' ')

  return (
    <div className="ss-biz absolute inset-0 overflow-hidden bg-[#070b14] text-white" style={{ fontFamily: "'Geist Variable', sans-serif" }}>
      <style>{BIZ_CSS}</style>
      <div className="ss-biz-kenburns absolute inset-0">
        <BgVideo ref={videoRef} src={videoSrc} />
      </div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(7,11,20,0.94) 0%, rgba(7,11,20,0.82) 40%, rgba(7,11,20,0.35) 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 70% 50%, transparent 30%, rgba(7,11,20,0.7) 100%)' }} />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      {/* Gold hairline frame that draws itself in */}
      <div className="ss-biz-frame pointer-events-none absolute inset-[3.5vmin] border" style={{ borderColor: `${GOLD}8c` }} />

      {/* Autofocus grid over the right-hand side */}
      <div className="ss-biz-af pointer-events-none absolute">
        <div className="grid grid-cols-5 gap-[2.6vmin]">
          {AF_POINTS.map((i) => (
            <div key={i} ref={(el) => { afRefs.current[i] = el }} className="h-[3.2vmin] w-[3.2vmin] rounded-[0.3vmin] border transition-colors duration-150" />
          ))}
        </div>
        <div ref={lockRef} className="absolute left-1/2 top-1/2 h-[12vmin] w-[24vmin] border" style={{ borderColor: GOLD, opacity: 0 }}>
          {['-left-px -top-px border-l-2 border-t-2', '-right-px -top-px border-r-2 border-t-2', '-left-px -bottom-px border-l-2 border-b-2', '-right-px -bottom-px border-r-2 border-b-2'].map((p) => (
            <div key={p} className={`absolute h-[2vmin] w-[2vmin] ${p}`} style={{ borderColor: GOLD }} />
          ))}
        </div>
      </div>

      {/* Editorial copy */}
      <div className="ss-biz-copy absolute left-[9vw] top-1/2 -translate-y-1/2">
        <motion.div
          className="mb-[3vmin] flex items-center gap-[2vmin] text-[1.9vmin] font-medium uppercase tracking-[0.42em]"
          style={{ color: GOLD }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <span className="inline-block h-px w-[7vmin]" style={{ background: GOLD }} />
          {t('screensaver.hero.businessKicker')}
        </motion.div>
        <h1 className="ss-biz-title text-[9vmin] font-semibold leading-[1.02]" style={{ fontFamily: "'Playfair Display Variable', Georgia, serif" }}>
          {words.map((word, i) => (
            <span key={i} className="-mb-[0.25em] inline-block overflow-hidden align-bottom">
              <motion.span
                className="ss-biz-shine inline-block pb-[0.25em]"
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.5 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {word}{i < words.length - 1 ? ' ' : ''}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-[3.4vmin] text-[3vmin] font-light text-white/70">
          <PhraseSwap phrase={phrase} />
        </div>
        <motion.div
          className="mt-[5vmin] inline-flex items-center gap-[2vmin] border px-[3.4vmin] py-[1.7vmin] text-[2.3vmin] font-medium uppercase tracking-[0.22em]"
          style={{ borderColor: `${GOLD}aa`, color: '#f3e7d3' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
        >
          {cta}
          <span className="ss-biz-arrow" style={{ color: GOLD }}>→</span>
        </motion.div>
      </div>

      {/* Camera readout */}
      <div className="pointer-events-none absolute bottom-[6vmin] left-[9vw] right-[9vw] flex items-center justify-between font-mono text-[1.6vmin] uppercase tracking-[0.3em] text-white/45">
        <span>ISO 100 · 1/160 · f/2.8 · 85mm</span>
        <span ref={focusTextRef} style={{ color: GOLD }}>AF ○</span>
      </div>
    </div>
  )
}

const BIZ_CSS = `
.ss-biz-kenburns { animation: ss-biz-kb 24s ease-in-out infinite alternate; }
@keyframes ss-biz-kb { from { transform: scale(1.04) translate(0, 0) } to { transform: scale(1.14) translate(-2%, 1%) } }
.ss-biz-frame { clip-path: inset(0 100% 100% 0); animation: ss-biz-draw 2.6s cubic-bezier(.65,0,.35,1) 0.2s forwards; }
@keyframes ss-biz-draw { 50% { clip-path: inset(0 0 100% 0) } to { clip-path: inset(0 0 0 0) } }
.ss-biz-af { right: 12vw; top: 50%; transform: translateY(-50%); }
.ss-biz-shine { background: linear-gradient(100deg, #fff 0%, #fff 40%, #f6e3c0 47%, #d4b483 50%, #f6e3c0 53%, #fff 60%, #fff 100%); background-size: 300% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: ss-biz-shine 7s ease-in-out 2s infinite; }
@keyframes ss-biz-shine { 0% { background-position: 100% 0 } 35%, 100% { background-position: 0% 0 } }
.ss-biz-arrow { display: inline-block; animation: ss-biz-nudge 1.8s ease-in-out infinite; }
@keyframes ss-biz-nudge { 0%, 100% { transform: translateX(0) } 50% { transform: translateX(0.8vmin) } }
@media (orientation: portrait) {
  .ss-biz-af { right: 50%; top: 22%; transform: translate(50%, -50%); }
  .ss-biz-copy { top: auto !important; bottom: 16vmin; transform: none !important; right: 9vw; }
}
`
